/**
 * "Replace" — swap the coupon code already issued for one device line for a different Shopify code.
 *
 * For a minted code that has to be re-issued: deactivated by mistake, sent to the wrong person,
 * minted at the wrong amount. The new code is created in Shopify first; this records it.
 *
 * ROOT ONLY. Hidden (not greyed out) for everyone else, the same reasoning as
 * DismissOrderButton: once a code is issued the troubleshoot tier can see it but not change it.
 * The server enforces the role, verifies the new code in Shopify, refuses a redeemed old code,
 * notes the swap on the order and deactivates the old code — hiding the button is tidiness, not
 * the control.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@staff-app/ui';
import { Replace } from 'lucide-react';
import type { DeviceOrderCoupon, DeviceOrderTask } from '@staff-app/shared-types';
import { useIsOrderRoot } from './DismissOrderButton';
import { useReplaceDeviceOrderCoupon } from '../hooks/useOrderDeviceTasks';

export function ReplaceCouponButton({
  task,
  productIndex,
  coupon,
}: {
  task: DeviceOrderTask;
  productIndex: number;
  coupon: DeviceOrderCoupon;
}) {
  const isRoot = useIsOrderRoot();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [replace, { loading, error }] = useReplaceDeviceOrderCoupon();

  // A redeemed code cannot be replaced (the customer already used it), nor can one the chase
  // cancelled — the server refuses both, so the button is not offered.
  if (!isRoot || coupon.used || task.couponsCancelledAt) return null;

  const submit = async () => {
    try {
      await replace({
        variables: {
          invoiceId: task._id,
          productIndex,
          newCouponCode: code.trim(),
          reason: reason.trim(),
        },
      });
      setOpen(false);
    } catch {
      // Shown via `error` below; the dialog stays open so the refusal can be read.
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 gap-1 px-2"
        title="Replace this coupon code with a different Shopify code"
        onClick={() => {
          setCode('');
          setReason('');
          setOpen(true);
        }}
        data-testid="coupon-replace"
      >
        <Replace className="h-3.5 w-3.5" />
        {'Replace'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{'Replace coupon code'}</DialogTitle>
            <DialogDescription>
              {'Create the new code in Shopify first. The old code is deactivated in Shopify and '}
              {'the change is recorded as an order note.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 text-sm">
            <div className="text-muted-foreground">
              {'Invoice '}
              <span className="font-mono">{task.invoiceId || task._id}</span>
              {task.ownerEmail || task.customerEmail ? ` · ${task.ownerEmail || task.customerEmail}` : ''}
            </div>
            <div>
              {'Current code '}
              <span className="font-mono" data-testid="coupon-replace-current">{coupon.code}</span>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{'New coupon code'}</span>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="font-mono"
                placeholder="e.g. QNG668R2F9P4"
                data-testid="coupon-replace-code"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{'Reason (recorded as an order note)'}</span>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="coupon-replace-reason"
              />
            </label>
            {error ? (
              <p className="text-sm text-destructive" data-testid="coupon-replace-error">
                {error.message}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
              {'Cancel'}
            </Button>
            <Button
              onClick={() => { void submit(); }}
              disabled={
                loading ||
                !code.trim() ||
                !reason.trim() ||
                code.trim().toUpperCase() === coupon.code.toUpperCase()
              }
              data-testid="coupon-replace-confirm"
            >
              {loading ? 'Replacing…' : 'Replace'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
